import { useCallback, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { cn } from 'src/helpers/style'

import {
  AppIcon,
  AppIconNames,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

import { useLazyImage } from '../hooks/use-lazy-image'
import { ICompanyCardProps } from '../types'

export const CompanyCard = ({
  company,
  onSelect,
}: ICompanyCardProps): JSX.Element => {
  const { t } = useTranslation()
  const { ref, isVisible } = useLazyImage<HTMLButtonElement>()
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  // Catches cache hits where the image's `load` event fires before React
  // attaches `onLoad`, which would otherwise leave the card stuck at opacity-0.
  const imageRef = useCallback((node: HTMLImageElement | null): void => {
    if (node?.complete) {
      setIsLoaded(true)
    }
  }, [])

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onSelect(company)}
      aria-label={t('whyWeInvest.card.openAriaLabel', { name: company.name })}
      className="group relative aspect-square w-full max-w-[228px] overflow-hidden rounded-[12px] border border-border-default bg-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white"
    >
      {isVisible ? (
        <img
          ref={imageRef}
          src={company.image}
          alt={company.name}
          onLoad={() => setIsLoaded(true)}
          className={cn(
            'h-full w-full object-cover transition duration-500',
            isLoaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}

      {isVisible && (
        <div className="pointer-events-none absolute inset-0 flex flex-col justify-end bg-black/0 p-3 opacity-0 transition duration-300 group-hover:bg-black/60 group-hover:opacity-100 group-focus-visible:bg-black/60 group-focus-visible:opacity-100">
          <Typography
            variant={TypographyVariant.bodyMd}
            weight={TypographyWeight.light}
            className="text-left text-white"
          >
            {company.name}
          </Typography>
          <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/80 text-white">
            <AppIcon
              name={AppIconNames.arrowUp}
              size={16}
              className="rotate-45"
            />
          </span>
        </div>
      )}
    </button>
  )
}
